import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Radar, ScrollText, Shield, ShieldCheck } from 'lucide-react';

import api from '@/lib/api';
import { getBranding } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const PASSWORD_HINT = '12+ characters, uppercase, lowercase, number, and special character.';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const branding = getBranding() || {};
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function verifyInvite() {
      if (!token) {
        setError('Invite token missing. Request a new invite from your administrator.');
        setLoading(false);
        return;
      }

      try {
        const res = await api.get('/auth/verify-invite', { params: { token } });
        if (cancelled) return;
        if (!res.data?.valid) {
          setError(res.data?.error || 'Invalid or expired invite');
          setInvite(null);
          return;
        }
        setInvite({ email: res.data.email, orgName: res.data.org_name });
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Unable to verify invite');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    verifyInvite();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => navigate('/login'), 3000);
    return () => window.clearTimeout(timer);
  }, [navigate, success]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/auth/accept-invite', { token, password });
      setSuccess(res.data?.message || 'Password set. You can now log in.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#050A10] md:flex-row">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1220] p-12 md:flex lg:p-20">
        <div className="absolute right-[-10%] top-[-10%] h-96 w-96 rounded-full bg-[#5EE1D9]/10 blur-[120px]" />
        <div className="absolute bottom-[-5%] left-[-5%] h-64 w-64 rounded-full bg-[#5EE1D9]/5 blur-[80px]" />

        <div className="relative z-10">
          <div className="mb-12 flex items-center gap-3 text-[#5EE1D9]">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#5EE1D9]/10">
              <Shield className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-black tracking-tight">{branding.company_name || 'ASM'}</h1>
          </div>

          <div className="space-y-6">
            <h2 className="text-5xl font-extrabold leading-tight text-white">
              {(branding.product_name || 'SecureOps Monitor').split(' ').slice(0, -1).join(' ') || 'SecureOps'}
              <br />
              <span className="text-[#5EE1D9]/80">{(branding.product_name || 'SecureOps Monitor').split(' ').pop()}</span>
            </h2>
            <p className="max-w-md text-lg text-slate-400">
              Finish your account setup with a strong password before accessing the dashboard.
            </p>
          </div>

          <div className="mt-16 space-y-8">
            <div className="flex items-start gap-4">
              <Radar className="mt-1 h-5 w-5 text-[#5EE1D9]" />
              <div>
                <h4 className="font-semibold text-white">Real-time device tracking</h4>
                <p className="text-sm text-slate-500">Comprehensive monitoring across all corporate endpoints.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ShieldCheck className="mt-1 h-5 w-5 text-[#5EE1D9]" />
              <div>
                <h4 className="font-semibold text-white">Advanced threat detection</h4>
                <p className="text-sm text-slate-500">Policy-driven anomaly detection and automated enforcement.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ScrollText className="mt-1 h-5 w-5 text-[#5EE1D9]" />
              <div>
                <h4 className="font-semibold text-white">Immutable audit logs</h4>
                <p className="text-sm text-slate-500">Append-only activity reports for compliance.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2">
          <span className="technical-label text-slate-500">SYSTEM STATUS:</span>
          <span className="flex h-2 w-2 rounded-full bg-[#5EE1D9] animate-pulse" />
          <span className="technical-label uppercase text-[#5EE1D9]">All nodes operational</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-center bg-[#050A10] px-6 py-12 lg:px-24">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex items-center gap-2 text-[#5EE1D9] md:hidden">
            <Shield className="h-5 w-5" />
            <span className="text-xl font-bold">{branding.company_name || 'ASM'}</span>
          </div>

          <Card className="border-[#1C2A44] bg-[#0B1220] text-white shadow-2xl shadow-black/30">
            <CardHeader className="space-y-2">
              <CardTitle className="text-3xl font-bold tracking-tight text-white">Accept Invite</CardTitle>
              <p className="text-sm text-slate-400">
                Set your password to activate your {branding.product_name || 'ASM'} account.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading && (
                <div className="rounded-lg border border-[#1C2A44] bg-[#101A2E] px-4 py-6 text-sm text-slate-300">
                  Verifying invite...
                </div>
              )}

              {!loading && error && !invite && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-4">
                    <p className="text-sm font-medium text-red-400">{error}</p>
                  </div>
                  <Button asChild className="w-full bg-[#5EE1D9] font-bold text-[#050A10] hover:bg-[#5EE1D9]/90">
                    <Link to="/login">Back to Login</Link>
                  </Button>
                </div>
              )}

              {!loading && success && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-4 py-4">
                    <p className="text-sm font-medium text-emerald-400">{success}</p>
                    <p className="mt-2 text-sm text-slate-300">Redirecting to login in 3 seconds...</p>
                  </div>
                  <Button asChild className="w-full bg-[#5EE1D9] font-bold text-[#050A10] hover:bg-[#5EE1D9]/90">
                    <Link to="/login">Go to Login Now</Link>
                  </Button>
                </div>
              )}

              {!loading && invite && !success && (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email" className="text-slate-300">Email Address</Label>
                    <Input
                      id="invite-email"
                      value={invite.email}
                      readOnly
                      className="h-12 border-[#1C2A44] bg-slate-800/50 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invite-org" className="text-slate-300">Organization</Label>
                    <Input
                      id="invite-org"
                      value={invite.orgName}
                      readOnly
                      className="h-12 border-[#1C2A44] bg-slate-800/50 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-300">New Password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Create a strong password"
                      className="h-12 border-[#1C2A44] bg-slate-800/50 text-white placeholder:text-slate-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-slate-300">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder="Re-enter your password"
                      className="h-12 border-[#1C2A44] bg-slate-800/50 text-white placeholder:text-slate-500"
                    />
                  </div>

                  <div className="rounded-lg border border-[#1C2A44] bg-[#101A2E] px-4 py-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-[#5EE1D9]">Password requirements</p>
                    <p className="mt-1 text-sm text-slate-400">{PASSWORD_HINT}</p>
                  </div>

                  {error && (
                    <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3">
                      <p className="text-sm font-medium text-red-400">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="h-12 w-full bg-[#5EE1D9] font-bold text-[#050A10] hover:bg-[#5EE1D9]/90"
                  >
                    {submitting ? 'Setting Password...' : 'Set Password'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
